import React, { useState, useEffect, useCallback } from "react";
import ProductPreview from "@/components/ProductPreview";
import { CaretLeft, CaretRight, ChartBar, Users, ListChecks, DeviceMobile, Circle } from "@phosphor-icons/react";

/**
 * A small "feature slides" carousel shown on product detail pages.
 * Rotates through Dashboard / Records / Reports / Mobile mockups so prospects
 * can see functionality at a glance. All light-themed.
 */
export default function ProductSlides({ product }) {
  const slides = [
    { title: "Dashboard", desc: "The at-a-glance snapshot your ops team lives on — realtime KPIs and next actions.", icon: ChartBar, render: () => <ProductPreview preview_key={product.preview_key} /> },
    { title: "Records", desc: "Search, filter and open any record — with instant edit, notes, audit history and role-based access.", icon: Users, render: () => <RecordsMock brand="#1E88F0" /> },
    { title: "Reports", desc: "Boardroom-ready reports auto-generated from your live data. Export to Excel or share via link.", icon: ListChecks, render: () => <ReportsMock /> },
    { title: "Mobile", desc: "Give managers and field teams a native mobile experience — designed for one-thumb use.", icon: DeviceMobile, render: () => <MobileMock title={product.title} /> },
  ];
  const [i, setI] = useState(0);
  const next = useCallback(() => setI((v) => (v + 1) % slides.length), [slides.length]);
  const prev = useCallback(() => setI((v) => (v - 1 + slides.length) % slides.length), [slides.length]);

  useEffect(() => {
    const id = setInterval(next, 6000);
    return () => clearInterval(id);
  }, [next]);

  const s = slides[i];
  const Icon = s.icon;

  return (
    <div data-testid="product-slides" className="mt-12 border border-[color:var(--line)] bg-white">
      <div className="p-6 md:p-8 border-b border-[color:var(--line)] flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">See it in action</div>
          <h3 className="mt-2 font-display font-black text-2xl md:text-3xl">A quick tour of what ships with {product.title}</h3>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="slides-prev" onClick={prev} aria-label="Previous slide" className="border border-[color:var(--line)] p-2 hover:border-black hover:bg-neutral-50 transition-colors">
            <CaretLeft size={16} weight="bold" />
          </button>
          <button data-testid="slides-next" onClick={next} aria-label="Next slide" className="border border-[color:var(--line)] p-2 hover:border-black hover:bg-neutral-50 transition-colors">
            <CaretRight size={16} weight="bold" />
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-0">
        <div className="lg:col-span-2 p-6 md:p-8 border-r border-[color:var(--line)] bg-[color:var(--paper-alt)]">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[color:var(--brand)]">
            <Icon size={14} weight="bold" /> Slide {i + 1} of {slides.length}
          </div>
          <h4 data-testid="slides-title" className="mt-3 font-display font-bold text-2xl leading-tight">{s.title}</h4>
          <p data-testid="slides-desc" className="mt-3 text-neutral-700 leading-relaxed">{s.desc}</p>
          <div className="mt-5 flex items-center gap-2">
            {slides.map((_, idx) => (
              <button
                key={idx}
                data-testid={`slides-dot-${idx}`}
                onClick={() => setI(idx)}
                aria-label={`Go to slide ${idx + 1}`}
                className={`w-2 h-2 rounded-full transition-all ${idx === i ? "bg-[color:var(--brand)] w-6" : "bg-neutral-300 hover:bg-neutral-400"}`}
              />
            ))}
          </div>
        </div>
        <div className="lg:col-span-3 p-4 md:p-6 bg-white">
          <div key={i} className="aspect-[16/10] border border-[color:var(--line)] overflow-hidden animate-fadein">
            {s.render()}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadein { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
        .animate-fadein { animation: fadein 0.35s ease; }
      `}</style>
    </div>
  );
}

function RecordsMock() {
  return (
    <div className="w-full h-full bg-white p-3 flex flex-col gap-2 text-[color:var(--ink)]">
      <div className="flex items-center gap-2">
        <div className="flex-1 border border-neutral-200 h-6 px-2 flex items-center font-mono text-[9px] text-neutral-500">Search records…</div>
        <div className="bg-[color:var(--brand)] text-white font-mono text-[9px] px-2 py-1">+ NEW</div>
      </div>
      <div className="border border-neutral-200 flex-1 overflow-hidden">
        <div className="grid grid-cols-12 bg-neutral-50 border-b border-neutral-200 font-mono text-[8px] uppercase tracking-widest text-neutral-500">
          {["ID","NAME","STATUS","OWNER","AMOUNT","UPDATED"].map((h, idx) => (
            <div key={h} className={`px-2 py-1.5 ${idx === 0 ? "col-span-1" : idx === 1 ? "col-span-3" : idx === 2 ? "col-span-2" : idx === 3 ? "col-span-2" : idx === 4 ? "col-span-2" : "col-span-2"}`}>{h}</div>
          ))}
        </div>
        {[
          ["#1042","Nadia Al-Muhairi","Active","Rahman","AED 12,400","10:34"],
          ["#1041","Blue Meadow LLC","Pending","Priya","AED 8,900","09:52"],
          ["#1040","Karan Malhotra","Active","Rahman","AED 22,100","09:11"],
          ["#1039","Sunrise Café","Won","Priya","AED 4,320","08:45"],
          ["#1038","Delta Trading","Active","Ali","AED 15,780","08:12"],
        ].map((row, r) => (
          <div key={r} className={`grid grid-cols-12 border-b border-neutral-100 text-[9px] items-center ${r === 0 ? "bg-[color:var(--brand)]/5" : ""}`}>
            <div className="col-span-1 px-2 py-1.5 font-mono text-neutral-500">{row[0]}</div>
            <div className="col-span-3 px-2 py-1.5 font-semibold">{row[1]}</div>
            <div className="col-span-2 px-2 py-1.5"><span className={`px-1.5 py-0.5 text-[8px] ${row[2] === "Active" ? "bg-emerald-100 text-emerald-700" : row[2] === "Pending" ? "bg-amber-100 text-amber-700" : "bg-[color:var(--brand)]/10 text-[color:var(--brand)]"}`}>{row[2]}</span></div>
            <div className="col-span-2 px-2 py-1.5 text-neutral-700">{row[3]}</div>
            <div className="col-span-2 px-2 py-1.5 font-mono text-neutral-700">{row[4]}</div>
            <div className="col-span-2 px-2 py-1.5 font-mono text-neutral-500">{row[5]}</div>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between text-[9px] font-mono text-neutral-500">
        <span>Showing 5 of 1,247</span>
        <span className="flex items-center gap-1"><Circle size={6} weight="fill" className="text-emerald-500" /> LIVE</span>
      </div>
    </div>
  );
}

function ReportsMock() {
  return (
    <div className="w-full h-full bg-white p-4 grid grid-cols-3 gap-2 text-[color:var(--ink)]">
      <div className="col-span-2 border border-neutral-200 p-2 flex flex-col">
        <div className="font-mono text-[8px] uppercase tracking-widest text-neutral-500">REVENUE · LAST 6 MONTHS</div>
        <div className="flex items-end gap-1 h-24 mt-2">
          {[3,5,4,7,6,9].map((h, i) => (
            <div key={i} className="flex-1 flex flex-col items-center">
              <div className="w-full bg-[color:var(--brand)]" style={{ height: `${h * 10}%` }} />
              <div className="font-mono text-[7px] text-neutral-500 mt-1">M{i + 1}</div>
            </div>
          ))}
        </div>
        <div className="mt-1 font-display font-black text-xl">AED 148,240 <span className="font-mono text-[9px] text-emerald-700 font-normal">+22% MoM</span></div>
      </div>
      <div className="flex flex-col gap-2">
        <div className="border border-neutral-200 p-2 flex-1 flex flex-col items-center justify-center">
          <div className="font-mono text-[7px] uppercase tracking-widest text-neutral-500">SATISFACTION</div>
          <svg viewBox="0 0 40 40" className="w-12 h-12 mt-1">
            <circle cx="20" cy="20" r="15" fill="none" stroke="#e5e5e5" strokeWidth="4" />
            <circle cx="20" cy="20" r="15" fill="none" stroke="#1E88F0" strokeWidth="4" strokeDasharray="88 100" transform="rotate(-90 20 20)" />
          </svg>
          <div className="font-display font-black text-sm">88%</div>
        </div>
        <div className="border border-neutral-200 p-2 flex-1">
          <div className="font-mono text-[7px] uppercase tracking-widest text-neutral-500">TOP MARKETS</div>
          {[["UAE",56],["IN",28],["EU",16]].map(([label, val]) => (
            <div key={label} className="mt-1">
              <div className="flex justify-between text-[8px] font-mono"><span>{label}</span><span>{val}%</span></div>
              <div className="h-1 bg-neutral-100"><div className="h-1 bg-[color:var(--brand)]" style={{ width: `${val}%` }} /></div>
            </div>
          ))}
        </div>
      </div>
      <div className="col-span-3 border border-neutral-200 p-2 flex items-center justify-between">
        <span className="font-mono text-[8px] uppercase tracking-widest text-neutral-500">Export</span>
        <div className="flex gap-1">
          <span className="border border-neutral-200 px-2 py-0.5 text-[8px]">CSV</span>
          <span className="border border-neutral-200 px-2 py-0.5 text-[8px]">Excel</span>
          <span className="border border-neutral-200 px-2 py-0.5 text-[8px]">PDF</span>
          <span className="bg-[color:var(--brand)] text-white px-2 py-0.5 text-[8px]">SHARE LINK</span>
        </div>
      </div>
    </div>
  );
}

function MobileMock({ title }) {
  return (
    <div className="w-full h-full bg-neutral-100 flex items-center justify-center p-2">
      <div className="bg-white border-2 border-neutral-800 rounded-[12px] p-1.5 w-36 h-56 shadow-lg">
        <div className="rounded-[8px] bg-white h-full flex flex-col overflow-hidden">
          <div className="bg-[color:var(--brand)] text-white text-center py-1 font-mono text-[7px] tracking-widest">9:41 · MAXINE</div>
          <div className="p-2 border-b border-neutral-100">
            <div className="font-display font-bold text-[10px] leading-tight truncate">{title}</div>
            <div className="font-mono text-[7px] text-neutral-500">Today · 12 items</div>
          </div>
          <div className="flex-1 p-2 space-y-1.5 overflow-hidden">
            {[["Approve leave", "Priya · 5d"],["Order #1042", "AED 12,400"],["New ticket", "AC repair"]].map(([a, b], i) => (
              <div key={i} className="border border-neutral-200 p-1.5">
                <div className="font-semibold text-[8px] truncate">{a}</div>
                <div className="font-mono text-[7px] text-neutral-500">{b}</div>
              </div>
            ))}
          </div>
          <div className="flex border-t border-neutral-200 justify-around py-1">
            <div className="w-3 h-3 bg-[color:var(--brand)]" />
            <div className="w-3 h-3 bg-neutral-200" />
            <div className="w-3 h-3 bg-neutral-200" />
            <div className="w-3 h-3 bg-neutral-200" />
          </div>
        </div>
      </div>
    </div>
  );
}
